import axiosInstance from './axiosInstance';

export interface DeliveryOrder {
  _id: string;
  id: string;
  orderNumber: string;
  patientName: string;
  patientPhone: string;
  patientAddress: string;
  status: string;
  deliveryStatus: string;
  priority: string;
  deliveryType: string;
  totalValue: number;
  deliveryDate: string;
  deliveryTime: string;
  createdAt: string;
  updatedAt: string;
  driverId?: string;
  driverName?: string;
  assignedAt?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;
  proofOfDelivery?: {
    type: 'signature' | 'photo' | 'otp';
    data: string;
    timestamp: string;
  };
}

export interface DeliveryStats {
  totalOrders: number;
  todayOrders: number;
  inTransitOrders: number;
  deliveredOrders: number;
  pendingOrders: number;
  failedOrders: number;
  successRate: number;
  avgDeliveryTime: number;
  totalRevenue: number;
  todayRevenue: number;
}

class DeliveryService {
  private baseEndpoint = '/delivery';

  /**
   * Get all delivery orders
   */
  async getAllDeliveryOrders(): Promise<DeliveryOrder[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/orders`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching delivery orders:', error);
      return [];
    }
  }

  /**
   * Get specific delivery order by ID
   */
  async getDeliveryOrderById(orderId: string): Promise<DeliveryOrder | null> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/orders/${orderId}`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching delivery order:', error);
      return null;
    }
  }

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: string, additionalData?: any): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/orders/${orderId}/status`, {
        status,
        ...additionalData
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error updating order status:', error);
      return false;
    }
  }

  /**
   * Assign order to driver
   */
  async assignOrderToDriver(orderId: string, assignmentData: { driverId: string; routeId?: string; routeName?: string; deliveryCharges?: number }): Promise<boolean> {
    try {
      const response = await axiosInstance.post(`${this.baseEndpoint}/orders/${orderId}/assign-pickup`, assignmentData);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error assigning order to driver:', error);
      return false;
    }
  }

  /**
   * Get delivery statistics
   */
  async getDeliveryStats(): Promise<DeliveryStats> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/stats`);
      return response.data?.data || this.getEmptyStats();
    } catch (error) {
      console.error('Error fetching delivery stats:', error);
      return this.getEmptyStats();
    }
  }

  private getEmptyStats(): DeliveryStats {
    return {
      totalOrders: 0,
      todayOrders: 0,
      inTransitOrders: 0,
      deliveredOrders: 0,
      pendingOrders: 0,
      failedOrders: 0,
      successRate: 0,
      avgDeliveryTime: 0,
      totalRevenue: 0,
      todayRevenue: 0
    };
  }
}

// Export singleton instance
export const deliveryService = new DeliveryService();

// Export convenience functions
export const getAllDeliveryOrders = () => deliveryService.getAllDeliveryOrders();
export const getDeliveryOrderById = (orderId: string) => deliveryService.getDeliveryOrderById(orderId);
export const updateOrderStatus = (orderId: string, status: string, additionalData?: any) => 
  deliveryService.updateOrderStatus(orderId, status, additionalData);
export const assignOrderToDriver = (orderId: string, assignmentData: { driverId: string; routeId?: string; routeName?: string; deliveryCharges?: number }) => 
  deliveryService.assignOrderToDriver(orderId, assignmentData);
export const getDeliveryStats = () => deliveryService.getDeliveryStats();
